import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const FullNameCompatibilityCalculator = () => {
  const [name1, setName1] = useState('');
  const [name2, setName2] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Helper function to convert letters to numbers
  const letterToNumber = (letter) => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return letters.indexOf(letter) + 1;
  };

  // Helper function to reduce numbers to single digit
  const reduceNumber = (num) => {
    while (num > 9 && ![11, 22, 33].includes(num)) {
      num = num.toString().split('').reduce((sum, digit) => sum + +digit, 0);
    }
    return num;
  };

  // Helper function to calculate name compatibility
  const calculateNameCompatibility = (name1, name2) => {
    const names = [name1.toUpperCase(), name2.toUpperCase()];
    const nameNumbers = [];
    
    // Calculate numbers for each letter in both names
    names.forEach((name, index) => {
      const letters = name.split('');
      const nameData = [];
      
      for (let i = 0; i < letters.length; i++) {
        if (letters[i] !== ' ') {
          const num = letterToNumber(letters[i]);
          const reduced = reduceNumber(num);
          nameData.push({
            letter: letters[i],
            number: reduced
          });
        }
      }

      // Calculate total number for the name
      const totalNumber = nameData.reduce((sum, item) => sum + item.number, 0);
      const totalReduced = reduceNumber(totalNumber);
      
      nameNumbers.push({
        name: index === 0 ? 'Name 1' : 'Name 2',
        nameData,
        totalNumber,
        totalReduced
      });
    });

    // Calculate compatibility number
    const compatibilityNumber = reduceNumber(nameNumbers[0].totalReduced + nameNumbers[1].totalReduced);
    
    // Calculate compatibility score (0-100)
    const compatibilityScore = Math.floor((compatibilityNumber / 9) * 100);

    return {
      nameNumbers,
      compatibilityNumber,
      compatibilityScore
    };
  };

  const handleName1Change = (e) => {
    setName1(e.target.value);
    setError('');
  };

  const handleName2Change = (e) => {
    setName2(e.target.value);
    setError('');
  };

  const calculateCompatibility = () => {
    if (!name1.trim() || !name2.trim()) {
      setError('Please enter both names');
      return;
    }

    const compatibilityData = calculateNameCompatibility(name1, name2);
    setResults(compatibilityData);

    // Prepare chart data
    const compatibilityChartData = {
      labels: ['Compatibility Score', 'Remaining'],
      datasets: [{
        label: 'Compatibility Distribution',
        data: [compatibilityData.compatibilityScore, 100 - compatibilityData.compatibilityScore],
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 99, 132, 0.7)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 4
      }]
    };

    setChartData(compatibilityChartData);
  };

  // Compatibility interpretations
  const compatibilityInterpretations = {
    1: {
      title: 'New Beginnings',
      description: 'This compatibility suggests new beginnings and fresh opportunities. The relationship may require effort to establish strong foundations.'
    },
    2: {
      title: 'Harmony',
      description: 'This compatibility indicates a harmonious and balanced relationship with strong emotional bonds and mutual understanding.'
    },
    3: {
      title: 'Creativity',
      description: 'This compatibility brings creativity and inspiration to the relationship, fostering growth and innovation.'
    },
    4: {
      title: 'Stability',
      description: 'This compatibility represents stability and practicality in the relationship, with strong organizational skills and reliability.'
    },
    5: {
      title: 'Change',
      description: 'This compatibility suggests a dynamic and adaptable relationship with opportunities for growth and exploration.'
    },
    6: {
      title: 'Harmony',
      description: 'This compatibility indicates strong family values and nurturing qualities, creating a harmonious and supportive relationship.'
    },
    7: {
      title: 'Understanding',
      description: 'This compatibility brings deep understanding and intellectual connection, fostering growth through shared wisdom.'
    },
    8: {
      title: 'Success',
      description: 'This compatibility represents success and achievement in both personal and professional aspects of the relationship.'
    },
    9: {
      title: 'Completion',
      description: 'This compatibility indicates a complete and fulfilling relationship with strong spiritual and emotional connections.'
    }
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Full Name Compatibility Calculator | Calculate Name Compatibility</title>
        <meta name="description" content="Calculate the numerological compatibility between two full names using our Numerology Calculator. Understand the relationship dynamics and potential." />
        <meta name="keywords" content="name compatibility, numerology calculator, relationship compatibility, name analysis, numerology compatibility" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/name-compatibility" />
        <meta property="og:title" content="Full Name Compatibility Calculator | Calculate Name Compatibility" />
        <meta property="og:description" content="Calculate the numerological compatibility between two full names using our Numerology Calculator. Understand the relationship dynamics and potential." />
        <meta property="og:image" content="https://yourwebsite.com/images/name-compatibility-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/name-compatibility" />
        <meta property="twitter:title" content="Full Name Compatibility Calculator | Calculate Name Compatibility" />
        <meta property="twitter:description" content="Calculate the numerological compatibility between two full names using our Numerology Calculator. Understand the relationship dynamics and potential." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/name-compatibility-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🤝</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Full Name Compatibility Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Name Compatibility</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name1">
            First Name:
          </label>
          <input
            type="text"
            id="name1"
            name="name1"
            value={name1}
            onChange={handleName1Change}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter first name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name2">
            Second Name:
          </label>
          <input
            type="text"
            id="name2"
            name="name2"
            value={name2}
            onChange={handleName2Change}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter second name"
          />
        </div>

        <button
          onClick={calculateCompatibility}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Name Compatibility
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Name Compatibility Results</h3>
            
            <div className="space-y-12">
              {/* Name Analysis Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Name Analysis</h4>
                <div className="space-y-8">
                  {results.nameNumbers.map((nameData, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg shadow-inner">
                      <h5 className="font-medium mb-4">{nameData.name}</h5>
                      <div className="space-y-2">
                        {nameData.nameData.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center">
                            <span className="text-gray-600">{item.letter}:</span>
                            <span className="font-medium">{item.number}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 flex justify-between items-center">
                        <span className="text-gray-600">Total Number:</span>
                        <span className="font-medium text-purple-600">{nameData.totalReduced}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Compatibility Visualization Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Compatibility Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Doughnut
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Compatibility Score',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      cutout: '60%',
                      animation: {
                        animateRotate: true,
                        animateScale: true
                      }
                    }}
                  />
                </div>
              </div>

              {/* Compatibility Analysis Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Compatibility Analysis</h4>
                <div className="space-y-8">
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Compatibility Number:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.compatibilityNumber}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Compatibility Score:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.compatibilityScore}%</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Interpretation:</h5>
                    <p className="text-gray-700">
                      {compatibilityInterpretations[results.compatibilityNumber]?.title}
                    </p>
                    <p className="mt-2 text-gray-600">
                      {compatibilityInterpretations[results.compatibilityNumber]?.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Harmonious Relationship</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Names: Sarah and Michael</p>
                <p className="text-purple-600 text-2xl mb-2">Compatibility Number: 6</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This combination shows strong family values and nurturing qualities, creating a harmonious and supportive relationship.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: Creative Partnership</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Names: David and Emily</p>
                <p className="text-purple-600 text-2xl mb-2">Compatibility Number: 3</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This combination brings creativity and inspiration to the relationship, fostering growth and innovation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate name compatibility:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Names</h3>
              <p className="text-gray-700">
                Input both names in the input fields.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Name Compatibility" button to see your results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review the compatibility number, score, and interpretation to understand the relationship dynamics.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Name Compatibility?</h3>
              <p className="text-gray-700">
                Name Compatibility analyzes the numerological relationship between two names to reveal their potential compatibility, relationship dynamics, and potential challenges.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                Each letter in both names is assigned a number, and these numbers are reduced to single digits. The compatibility is then calculated based on the combined vibration of both names.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What does it reveal?</h3>
              <p className="text-gray-700">
                It shows the potential compatibility, relationship dynamics, strengths, and challenges in the relationship between the two names.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate name compatibility calculation</li>
                <li>Individual name analysis</li>
                <li>Compatibility visualization</li>
                <li>Detailed interpretation</li>
                <li>Personalized insights</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand relationship dynamics</li>
                <li>Make informed decisions</li>
                <li>Enhance compatibility</li>
                <li>Support relationship growth</li>
                <li>Improve understanding</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullNameCompatibilityCalculator;
